import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { WEB_STORE_THEMES } from "../utils/webStoreThemes";

const STORAGE_KEY = "stylon_theme";
// The themes live in utils/webStoreThemes — this file used to keep the ninth
// copy of them and Pages/Shop kept a sixth. One list, imported.
const THEMES = Object.fromEntries(WEB_STORE_THEMES.map((t) => [t, t]));
const VALID_STYLES = WEB_STORE_THEMES;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { theme: THEMES.original, isClassic: false };
  return ctx;
};

const getStyleFromUrl = (search) => {
  const params = new URLSearchParams(search);
  const style = (params.get("style") || "").toLowerCase();
  return VALID_STYLES.includes(style) ? style : null;
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const salon = useSelector((state) => state.getSalonReducer?.data);
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return THEMES.original;
    const fromUrl = getStyleFromUrl(typeof window !== "undefined" ? window.location.search : "");
    if (fromUrl) return fromUrl;
    return document.documentElement.getAttribute("data-theme") || THEMES.original;
  });

  useEffect(() => {
    const urlStyle = getStyleFromUrl(location.search);
    const salonTheme =
      salon?.webStoreTheme && VALID_STYLES.includes(salon.webStoreTheme)
        ? salon.webStoreTheme
        : null;
    const docTheme =
      typeof document !== "undefined"
        ? document.documentElement.getAttribute("data-theme")
        : null;
    // Prefer salon theme from API over doc (initial HTML may default to "original" when no server injection)
    const fallback = salonTheme ?? (docTheme && VALID_STYLES.includes(docTheme) ? docTheme : null);
    const fallbackValid =
      fallback && VALID_STYLES.includes(fallback) ? fallback : THEMES.original;
    const next = urlStyle ?? fallbackValid;
    document.documentElement.setAttribute("data-theme", next);
    if (urlStyle) {
      try {
        localStorage.setItem(STORAGE_KEY, urlStyle);
      } catch (e) {}
    } else if (fallbackValid !== THEMES.original) {
      try {
        localStorage.setItem(STORAGE_KEY, fallbackValid);
      } catch (e) {}
    } else {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    }
    setTheme(next);
  }, [location.search, salon?.webStoreTheme]);

  return (
    <ThemeContext.Provider value={{ theme, isClassic: [THEMES.classic, THEMES.purple, THEMES.deepPurple, THEMES.pink, THEMES.blue, THEMES.lime, THEMES.forest, THEMES.bronze].includes(theme) }}>
      {children}
    </ThemeContext.Provider>
  );
};

export { THEMES };
